// ./components/HeroSection.jsx
import React, { useEffect, useRef, useState } from 'react';
import { motion } from "framer-motion";
import { FiBatteryCharging, FiWifi } from "react-icons/fi";
import { SiReact, SiNextdotjs,SiTypescript,SiNodedotjs,SiExpress,SiMongodb,SiPostgresql,SiTailwindcss } from "react-icons/si";
import GlitchImage from '../assets/images/about-glitch.png';
import Texture from '../assets/images/texture.jpg';
import CustomFont from '../assets/fonts/GRADIOO.ttf';
import QuoteTexture from '../assets/images/quote-texture.jpg';
import QuoteFont from '../assets/fonts/AwanNoyeed.otf';
import PixelBugFont from '../assets/fonts/Retro Guns.ttf';

// Simple utility function to combine class names
const cn = (...classes) => {
  return classes.filter(Boolean).join(' ');
};

// Technology badge component with icon
const TechBadge = ({ children, icon }) => {
  return (
    <span className="relative px-4 py-2 bg-[#630000] text-[#EDEBDD] rounded-full text-sm font-medium hover:bg-[#1B1717] transition-colors duration-300 inline-flex items-center gap-2">
      {icon}
      {children}
      <svg
        viewBox="0 0 286 73"
        fill="none"
        className="absolute -left-1 -right-1 -top-1 -bottom-1 pointer-events-none"
      >
        <path
          d="M142.293 1C106.854 16.8908 6.08202 7.17705 1.23654 43.3756C-2.10604 68.3466 29.5633 73.2652 122.688 71.7518C215.814 70.2384 316.298 70.689 275.761 38.0785C230.14 1.37835 97.0503 24.4575 52.9384 1"
          stroke="#EDEBDD"
          strokeWidth="2"
        />
      </svg>
    </span>
  );
};

// PointerHighlight Component
const PointerHighlight = ({
  children,
  rectangleClassName,
  pointerClassName,
  containerClassName
}) => {
  const containerRef = useRef(null);
  const [dimensions, setDimensions] = useState({ width: 0, height: 0 });

  useEffect(() => {
    const container = containerRef.current;
    
    if (container) {
      const { width, height } = container.getBoundingClientRect();
      setDimensions({ width, height });
    }
    
    const resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const { width, height } = entry.contentRect;
        setDimensions({ width, height });
      }
    });
    
    if (container) {
      resizeObserver.observe(container);
    }
    
    return () => {
      if (container) {
        resizeObserver.unobserve(container);
      }
    };
  }, []);

  return (
    <div className={cn("relative w-fit", containerClassName)} ref={containerRef}>
      {children}
      {dimensions.width > 0 && dimensions.height > 0 && (
        <motion.div
          className="pointer-events-none absolute inset-0 z-0"
          initial={{ opacity: 0, scale: 0.95, originX: 0, originY: 0 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.5, ease: "easeOut" }}>
          <motion.div
            className={cn(
              "absolute inset-0 border border-neutral-800 dark:border-neutral-200",
              rectangleClassName
            )}
            initial={{
              width: 0,
              height: 0,
            }}
            whileInView={{
              width: dimensions.width,
              height: dimensions.height,
            }}
            transition={{
              duration: 1,
              ease: "easeInOut",
            }} />
          <motion.div
            className="pointer-events-none absolute"
            initial={{ opacity: 0 }}
            whileInView={{
              opacity: 1,
              x: dimensions.width + 4,
              y: dimensions.height + 4,
            }}
            style={{
              rotate: -90,
            }}
            transition={{
              opacity: { duration: 0.1, ease: "easeInOut" },
              duration: 1,
              ease: "easeInOut",
            }}>
            <Pointer className={cn("h-5 w-5 text-blue-500", pointerClassName)} />
          </motion.div>
        </motion.div>
      )}
    </div>
  );
};

const Pointer = ({
  ...props
}) => {
  return (
    <svg
      stroke="currentColor"
      fill="currentColor"
      strokeWidth="1"
      strokeLinecap="round"
      strokeLinejoin="round"
      viewBox="0 0 16 16"
      height="1em"
      width="1em"
      xmlns="http://www.w3.org/2000/svg"
      {...props}>
      <path
        d="M14.082 2.182a.5.5 0 0 1 .103.557L8.528 15.467a.5.5 0 0 1-.917-.007L5.57 10.694.803 8.652a.5.5 0 0 1-.006-.916l12.728-5.657a.5.5 0 0 1 .556.103z"></path>
    </svg>
  );
};

const FloatingPhone = () => {
  return (
    <div
      style={{
        transformStyle: "preserve-3d",
        transform: "rotateY(-30deg) rotateX(15deg)",
      }}
      className="rounded-[24px] bg-[#630000]"
    >
      <motion.div
        initial={{
          transform: "translateZ(8px) translateY(-2px)",
        }}
        animate={{
          transform: "translateZ(32px) translateY(-8px)",
        }}
        transition={{
          repeat: Infinity,
          repeatType: "mirror",
          duration: 2,
          ease: "easeInOut",
        }}
        className="relative h-96 w-56 rounded-[24px] border-2 border-b-4 border-r-4 border-white border-l-neutral-200 border-t-neutral-200 bg-[#1B1717] p-1 pl-[3px] pt-[3px]"
      >
        <HeaderBar />
        <Screen />
      </motion.div>
    </div>
  );
};

const HeaderBar = () => {
  return (
    <>
      <div className="absolute left-[50%] top-2.5 z-10 h-2 w-16 -translate-x-[50%] rounded-md bg-[#1B1717]"></div>
      <div className="absolute right-3 top-2 z-10 flex gap-2">
        <FiWifi className="text-neutral-400" />
        <FiBatteryCharging className="text-neutral-400" />
      </div>
    </>
  );
};

const Screen = () => {
  return (
    <div className="relative z-0 grid h-full w-full place-content-center overflow-hidden rounded-[20px] bg-[#EDEBDD]">
      {/* Display the glitch image */}
      <img 
        src={GlitchImage} 
        alt="Profile" 
        className="w-full h-full object-cover"
      />
      
      {/* Contact Information - Fully Responsive */}
      <div className="absolute bottom-2 sm:bottom-4 left-2 sm:left-4 right-2 sm:right-4 z-10 space-y-1 sm:space-y-2">
        {/* Email */}
        <div className="flex items-center justify-center gap-1 sm:gap-2 rounded-md sm:rounded-lg border-[1px] bg-[#EDEBDD]/90 py-1 sm:py-2 text-[10px] sm:text-xs md:text-sm font-medium text-[#630000] backdrop-blur">
          <svg 
            className="w-2 h-2 sm:w-3 sm:h-3 md:w-4 md:h-4 flex-shrink-0" 
            fill="currentColor" 
            viewBox="0 0 20 20" 
            xmlns="http://www.w3.org/2000/svg"
          >
            <path d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z"></path>
            <path d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z"></path>
          </svg>
          <span className="truncate min-w-0">gl1tch@gl1tchmaster.xyz</span>
        </div>
        
        {/* Discord */}
        <div className="flex items-center justify-center gap-1 sm:gap-2 rounded-md sm:rounded-lg border-[1px] bg-[#630000]/90 py-1 sm:py-2 text-[10px] sm:text-xs md:text-sm font-medium text-[#EDEBDD] backdrop-blur">
          <svg 
            className="w-2 h-2 sm:w-3 sm:h-3 md:w-4 md:h-4 flex-shrink-0" 
            fill="currentColor" 
            viewBox="0 0 24 24"
          >
            <path d="M20.317 4.492c-1.53-.69-3.17-1.2-4.885-1.49a.075.075 0 0 0-.079.036c-.21.369-.444.85-.608 1.23a18.566 18.566 0 0 0-5.487 0 12.36 12.36 0 0 0-.617-1.23A.077.077 0 0 0 8.562 3c-1.714.29-3.354.8-4.885 1.491a.07.07 0 0 0-.032.027C.533 9.093-.32 13.555.099 17.961a.08.08 0 0 0 .031.055 20.03 20.03 0 0 0 5.993 2.98.078.078 0 0 0 .084-.026 14.78 14.78 0 0 0 1.226-1.963.074.074 0 0 0-.041-.104 13.201 13.201 0 0 1-1.872-.878.075.075 0 0 1-.008-.125c.126-.093.252-.19.372-.287a.075.075 0 0 1 .078-.01c3.927 1.764 8.18 1.764 12.061 0a.075.075 0 0 1 .079.009c.12.098.245.195.372.288a.075.075 0 0 1-.006.125c-.598.344-1.22.635-1.873.877a.075.075 0 0 0-.041.105c.36.687.772 1.341 1.225 1.962a.077.077 0 0 0 .084.028 19.963 19.963 0 0 0 6.002-2.981.076.076 0 0 0 .032-.054c.5-5.094-.838-9.52-3.549-13.442a.06.06 0 0 0-.031-.028zM8.02 15.278c-1.182 0-2.157-1.067-2.157-2.38 0-1.312.956-2.38 2.157-2.38 1.201 0 2.176 1.068 2.157 2.38 0 1.313-.956 2.38-2.157 2.38zm7.975 0c-1.183 0-2.157-1.067-2.157-2.38 0-1.312.955-2.38 2.157-2.38 1.201 0 2.176 1.068 2.157 2.38 0 1.313-.956 2.38-2.157 2.38z"/>
          </svg>
          <span className="truncate min-w-0">.5ko</span>
        </div>
      </div>
    </div>
  );
};

const HeroSection = () => {
  // Font faces for ALL custom fonts
  const fontStyles = `
    @font-face {
      font-family: 'CustomGrad';
      src: url(${CustomFont}) format('truetype');
      font-weight: 700;
      font-style: normal;
      font-display: swap;
    }
    @font-face {
      font-family: 'QuoteFont';
      src: url(${QuoteFont}) format('truetype');
      font-weight: 400;
      font-style: normal;
      font-display: swap;
    }
    @font-face {
      font-family: 'PixelBug';
      src: url(${PixelBugFont}) format('opentype');
      font-weight: 400;
      font-style: normal;
      font-display: swap;
    }
  `;

  // HERO TITLE styles (using original texture & font)
  const heroTexture = {
    backgroundImage: `url(${Texture})`,
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    WebkitBackgroundClip: 'text',
    backgroundClip: 'text',
    color: 'transparent',
    WebkitTextFillColor: 'transparent',
    fontFamily: 'CustomGrad, sans-serif',
  };

  // QUOTE SLIDER styles (using DIFFERENT texture & font)
  const quoteStyles = {
    backgroundImage: `url(${QuoteTexture})`,
    backgroundSize: '300% 300%',
    backgroundPosition: 'center',
    WebkitBackgroundClip: 'text',
    backgroundClip: 'text',
    color: 'transparent',
    WebkitTextFillColor: 'transparent',
    fontFamily: 'QuoteFont, serif',
    animation: 'quoteTextureFlow 25s linear infinite',
  };

  // PIXEL BUG FONT styles for paragraphs
  const pixelBugStyles = {
    fontFamily: 'PixelBug, monospace',
  };

  // Animation refs
  const titleRef = useRef(null);
  const paragraphRef = useRef(null);
  const skillsRef = useRef(null);

  // Smooth entrance animations
  useEffect(() => {
    const elements = [
      { ref: titleRef.current, delay: 500 },
      { ref: paragraphRef.current, delay: 1000 },
      { ref: skillsRef.current, delay: 1500 }
    ].filter(item => item.ref);

    const prefersReducedMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    
    if (prefersReducedMotion) {
      elements.forEach(item => item.ref?.classList.add('animate-in'));
      return;
    }

    // Trigger animations on page load
    const triggerAnimations = () => {
      elements.forEach(({ ref, delay }) => {
        setTimeout(() => {
          ref?.classList.add('animate-in');
        }, delay);
      });
    };

    triggerAnimations();

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const element = entry.target;
            element.classList.remove('animate-in');
            
            setTimeout(() => {
              element.classList.add('animate-in');
            }, 100);
          }
        });
      },
      { threshold: 0.3 }
    );

    elements.forEach(({ ref }) => {
      if (ref) observer.observe(ref);
    });

    return () => observer.disconnect();
  }, []);

  return (
    <section className="relative min-h-screen flex items-center justify-between bg-[#EDEBDD] px-8 md:px-16">
      {/* ALL STYLES */}
      <style>{fontStyles}</style>
      <style>{`
        /* SMOOTH ENTRANCE ANIMATIONS */
        .hero-element {
          opacity: 0;
          transform: translate3d(0, 0, 0) scale(0.92);
          filter: blur(12px);
          will-change: transform, opacity, filter;
          transition: 
            transform 1.5s cubic-bezier(0.16, 1, 0.3, 1),
            opacity 1.5s cubic-bezier(0.16, 1, 0.3, 1),
            filter 1.5s cubic-bezier(0.16, 1, 0.3, 1);
        }
        
        /* Direction-specific starting positions */
        .slide-from-left { 
          transform: translate3d(-120px, 0, 0) scale(0.95);
        }
        .slide-from-up { 
          transform: translate3d(0, -60px, 0) scale(0.95);
        }
        .slide-from-down { 
          transform: translate3d(0, 60px, 0) scale(0.95);
        }
        
        /* Final animated state */
        .animate-in { 
          opacity: 1; 
          transform: translate3d(0, 0, 0) scale(1); 
          filter: blur(0);
        }
        
        /* Quote Texture Animation */
        @keyframes quoteTextureFlow {
          0% { background-position: 0% 50%; }
          25% { background-position: 100% 25%; }
          50% { background-position: 50% 100%; }
          75% { background-position: 25% 0%; }
          100% { background-position: 0% 50%; }
        }
        
        /* Disable animations for reduced motion */
        @media (prefers-reduced-motion: reduce) {
          .hero-element, .animate-in { 
            transition: none !important; 
            filter: none !important; 
            transform: none !important; 
            opacity: 1 !important; 
          }
          * { animation: none !important; }
        }
      `}</style>

      {/* Left side - About Me Content */}
      <div className="flex-1 max-w-2xl pr-8">
        {/* Main Title with PointerHighlight */}
        <div
          ref={titleRef}
          className="hero-element slide-from-left mb-8"
        >
          <h1 className="text-5xl md:text-7xl font-extrabold leading-tight tracking-tight mb-4">
            <PointerHighlight
              rectangleClassName="bg-[#630000]/10 border-[#630000] border-2 leading-loose"
              pointerClassName="text-[#630000] h-4 w-4 md:h-6 md:w-6"
              containerClassName="inline-block"
            >
              <span 
                className="relative z-10"
                style={heroTexture}
              >
                About Me
              </span>
            </PointerHighlight>
          </h1>
          <div 
            className="text-2xl md:text-3xl font-bold"
            style={quoteStyles}
          >
            I'm Adam Harmane AKA <strong>GL1TCH</strong>
          </div>
        </div>

        {/* Description Paragraph */}
        <div
          ref={paragraphRef}
          className="hero-element slide-from-up mb-8"
        >
          <p 
            className="text-lg md:text-xl leading-relaxed text-[#1B1717] mb-6"
            style={pixelBugStyles}
          >
            I'm an 18-year-old full-stack developer based in <strong>Rabat, Morocco</strong>. 
            Passionate about crafting seamless web experiences, I specialize in both front-end and back-end development, 
            bringing ideas to life with clean code and modern technologies.
          </p>
          <p 
            className="text-lg md:text-xl leading-relaxed text-[#1B1717] mb-6"
            style={pixelBugStyles}
          >
            From building browser extensions to designing e-commerce platforms, I thrive on solving complex problems 
            and creating user-friendly applications. My journey as a developer is driven by curiosity, creativity, 
            and a commitment to excellence.
          </p>
          <p 
            className="text-lg md:text-xl leading-relaxed text-[#1B1717]"
            style={pixelBugStyles}
          >
            As a young coder from Morocco, I'm excited to contribute to the global tech community and inspire others 
            through innovative projects and mentorship.
          </p>
        </div>

        {/* Skills/Technologies */}
        <div
          ref={skillsRef}
          className="hero-element slide-from-down"
        >
          <h3 
            className="text-xl font-bold mb-4"
            style={heroTexture}
          >
            Technologies I Work With
          </h3>
          <div className="flex flex-wrap gap-3">
            <TechBadge icon={<SiReact className="w-4 h-4" />}>
              React
            </TechBadge>
            
            <TechBadge icon={<SiNextdotjs className="w-4 h-4" />}>
              Next.js
            </TechBadge>
            
            <TechBadge icon={<SiTypescript className="w-4 h-4" />}>
              TypeScript
            </TechBadge>
            
            <TechBadge icon={<SiNodedotjs className="w-4 h-4" />}>
              Node.js
            </TechBadge>
            
            <TechBadge icon={<SiExpress className="w-4 h-4" />}>
              Express
            </TechBadge>
            
            <TechBadge icon={<SiMongodb className="w-4 h-4" />}>
              MongoDB
            </TechBadge>
            
            <TechBadge icon={<SiPostgresql className="w-4 h-4" />}>
              PostgreSQL
            </TechBadge>
            
            <TechBadge icon={<SiTailwindcss className="w-4 h-4" />}>
              Tailwind CSS
            </TechBadge>
          </div>
          
        </div>
      </div>

      {/* Phone on the right */}
      <div className="flex-1 flex items-center justify-end">
        <FloatingPhone />
      </div>
    </section>
  );
};

export default HeroSection;